/**
 * gammaDerivation.ts — MER Algo Footprints v7.1B — Phase 6.3A
 *
 * Canonical Gamma derivation helper for the scoring pipeline.
 *
 * This is the single authoritative path for converting raw options/market data
 * into a GammaInfluenceInput suitable for the confidence-adjustment pass.
 *
 * ── What this owns ────────────────────────────────────────────────────────────
 * 1. OptionRow[] → RawOptionContract[]  (per-strike aggregated → per-contract)
 * 2. RawOptionContract[] → NormalizedOptionContract[]  (normalizeChain)
 * 3. NormalizedOptionContract[] → StrikeLevel[]  (buildStrikeMap)
 * 4. StrikeLevel[] → GammaSnapshot  (scoreGamma)
 * 5. GammaSnapshot → GammaInfluenceInput  (projection to scoring contract)
 *
 * ── Properties ───────────────────────────────────────────────────────────────
 * Pure:       no side effects, no async, no network calls
 * Domain-owned: lives in market/engines/gamma — no UI, no server, no React
 * Typed:      inputs and output are fully typed
 * Convergent: both buildSymbolContext and tests call this one function
 *
 * ── Usage ────────────────────────────────────────────────────────────────────
 * import { deriveGammaInfluenceInput } from '../engines/gamma/gammaDerivation';
 * const input = deriveGammaInfluenceInput(options, symbol, spot);
 * // input is null when options empty, spot invalid, or chain too sparse
 *
 * Placement: src/domains/market/engines/gamma/gammaDerivation.ts
 */

import { OptionRow }            from '../../../../types';
import type { RawOptionContract } from './gammaTypes';
import { normalizeChain }       from './optionsChainNormalizer';
import { buildStrikeMap }       from './strikeMapBuilder';
import { scoreGamma }           from './gammaScoring';
import type { GammaUnifiedContext } from './gammaInfluenceTypes';
import { fromGammaSnapshot } from './gammaContextMapper';

// ─── Configuration ────────────────────────────────────────────────────────────

/**
 * Maximum DTE (calendar days) for contracts included in the influence derivation.
 * Captures all weekly and monthly cycles without including LEAPS or far-dated noise.
 */
export const GAMMA_DERIVATION_MAX_DTE = 45;

// ─── Internal: OptionRow expansion ───────────────────────────────────────────

/**
 * Converts an OptionRow[] (one row per strike, with callOI/putOI aggregated)
 * into a RawOptionContract[] (one row per contract side per strike).
 *
 * This is the standard expansion needed before normalizeChain can run.
 * When expiration is absent or invalid, falls back to today's date so the
 * contract passes the DTE filter (DTE = 0, included as same-day expiry).
 */
function optionRowsToRawContracts(
  options: OptionRow[],
  symbol: string,
): RawOptionContract[] {
  const today   = new Date().toISOString().slice(0, 10);
  const EXP_RE  = /^\d{4}-\d{2}-\d{2}$/;
  const result: RawOptionContract[] = [];

  for (const row of options) {
    if (!row.strike || row.strike <= 0) continue;
    const exp = (row.expiration && EXP_RE.test(row.expiration)) ? row.expiration : today;

    result.push({
      symbol, underlying: symbol, strike: row.strike,
      expiration: exp, optionType: 'C',
      bid: null, ask: null, last: null,
      openInterest: row.callOI,
      volume:       row.callVol,
      impliedVol:   row.iv    ?? null,
      delta:        row.delta ?? null,
      gamma:        row.gamma,
      theta:        row.theta ?? null,
      vega:         row.vega  ?? null,
    });

    result.push({
      symbol, underlying: symbol, strike: row.strike,
      expiration: exp, optionType: 'P',
      bid: null, ask: null, last: null,
      openInterest: row.putOI,
      volume:       row.putVol,
      impliedVol:   row.iv    ?? null,
      delta:        row.delta != null ? -row.delta : null,
      gamma:        row.gamma,
      theta:        row.theta ?? null,
      vega:         row.vega  ?? null,
    });
  }

  return result;
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Derives a GammaUnifiedContext from raw OptionRow data and current spot price.
 * This is the canonical scoring-side Gamma derivation path.
 *
 * Returns null when options is empty, spot is invalid, or the chain is too sparse.
 * Returns a GammaUnifiedContext (superset of GammaInfluenceInput) otherwise.
 * Scoring consumers may pass the result directly to applyGammaInfluencePass and
 * applyGammaRankingPass because GammaUnifiedContext satisfies GammaInfluenceInput.
 */
export function deriveGammaInfluenceInput(
  options: OptionRow[],
  symbol:  string,
  spot:    number,
): GammaUnifiedContext | null {
  if (!options.length)         return null;
  if (!spot || spot <= 0)      return null;

  const rawContracts = optionRowsToRawContracts(options, symbol);
  const chain        = normalizeChain(rawContracts, spot, GAMMA_DERIVATION_MAX_DTE);
  if (!chain.length)           return null;

  const levels   = buildStrikeMap(chain, spot);
  const snapshot = scoreGamma({
    symbol,
    spot,
    chain,
    levels,
    maxDTE:      GAMMA_DERIVATION_MAX_DTE,
    computedAt:  Date.now(),
  });

  return fromGammaSnapshot(snapshot);
}
